import LoadMoreButton from '@rio-cloud/rio-uikit/LoadMoreButton';
import NoData from '@rio-cloud/rio-uikit/NoData';
import TableHead from '@rio-cloud/rio-uikit/TableHead';
import useSorting from '@rio-cloud/rio-uikit/useSorting';
import type React from 'react';
import { useState } from 'react';

import type { CardType } from '@/api/schema';
import CardSvg from '@/components/ui/svg/CardSvg';
import { t } from '@/lib/utils';

const INITIAL_TABLE_SIZE = 5;

const renderCell = (value: string | undefined | null) =>
    value || <NoData text={t('general.notDefined')} className='text-size-14' />;

const CardTable = ({ cards }: { cards: CardType[] }) => {
    const { sortedItems, sortKey, sortDirection, setSortKey, toggleDirection } = useSorting(cards, 'cardNumber');
    const [tableSize, setTableSize] = useState(INITIAL_TABLE_SIZE);

    const columns = [
        { key: 'cardNumber', label: t('general.cardNumber') },
        { key: 'firstRow', label: `${t('general.line')} 1` },
        { key: 'secondRow', label: `${t('general.line')} 2` },
    ];

    const handleSortChange = (event: React.MouseEvent<HTMLTableCellElement>) => {
        const newSortBy = event.currentTarget.getAttribute('data-sortby');
        if (newSortBy) {
            setSortKey(newSortBy as keyof CardType);
        }
        if (newSortBy === sortKey) {
            toggleDirection();
        }
    };

    const handleLoadMore = () => {
        setTableSize(t => t + INITIAL_TABLE_SIZE);
    };

    const visibleItems = sortedItems.slice(0, tableSize);

    return (
        <>
            <div className='table-responsive' data-testid='data-table'>
                <table className='table table-layout-fixed'>
                    <thead className='bg-lighter'>
                        <tr>
                            {columns.map(col => (
                                <TableHead
                                    key={col.key}
                                    column={col.key}
                                    label={col.label}
                                    sortBy={sortKey as string}
                                    sortDir={sortDirection}
                                    onClick={handleSortChange}
                                />
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleItems.map((row: CardType) => (
                            <tr key={row.cardNumber}>
                                <td className='border-top-only border-width-3 border-color-lightest'>
                                    <div className='display-flex align-items-center gap-5'>
                                        <CardSvg /> {row.cardNumber}
                                    </div>
                                </td>
                                <td className='border-top-only border-width-3 border-color-lightest'>
                                    {renderCell(row.firstRow)}
                                </td>
                                <td className='border-top-only border-width-3 border-color-lightest'>
                                    {renderCell(row.secondRow)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {cards.length > INITIAL_TABLE_SIZE && (
                <LoadMoreButton
                    loaded={visibleItems.length}
                    total={sortedItems.length}
                    onLoadMore={handleLoadMore}
                    loadMoreMessage={t('general.loadMore')}
                    noMoreMessage={t('orderHistory.allCardsLoaded')}
                />
            )}
        </>
    );
};

export default CardTable;
